// EraserMenu
import './BrushMenu.css';
import React, { useState, useEffect } from 'react';
import useStore from '../../../store';
import {
  FaBrush, FaSprayCan, FaPencilAlt, FaFeatherAlt, FaPenFancy, FaPaintBrush,
  FaDotCircle, FaPaw, FaCog, FaDrawPolygon,
  FaExclamation, FaTrash, FaFolderPlus, FaPlus, FaMinus
} from 'react-icons/fa';
import {
  FaBrush as FaBrush6, FaMarker, FaEraser, FaBroomBall, FaRegLightbulb, FaBorderAll, FaPaintRoller
} from 'react-icons/fa6';
import {
  TbLineDashed, TbBrush, TbSparkles, TbTexture, TbRosette, TbGrain,
} from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import { createBetterClick } from '../../../utils/interface/createBetterClick';
import useDragAndDropSort from '../../../hooks/interface/useDragAndDropSort';
import ObjectID from 'bson-objectid';

const brushIcons = {
  'liner': FaEraser,
  'plain': FaMarker,
  'spray': FaSprayCan,
  'bristle': FaPaintBrush,
  'feather': FaFeatherAlt,
  'ink': FaPenFancy,
  'pencil': FaPencilAlt,
  'oil': FaBrush,
  'rembrandt': FaPaintRoller,
  'watercolor': TbBrush,
  'outlined': FaDotCircle,
  'dashed': TbLineDashed,
  'neon': FaRegLightbulb,
  'sparkle': TbSparkles,
  'glyph': TbRosette,
  'pixels': FaBorderAll,

  'shape': FaDrawPolygon,
  'texture': TbTexture,
  'test': FaPaw,
  'error': FaExclamation,

  // Новые кисти
  'wet': FaBroomBall,
  'flat': FaBrush6,
  'velvetPastel': TbGrain,
  'waterSpread': TbBrush,
  'waterSoft': TbBrush,
};

function EraserMenu({ store }) {
  const { t } = useTranslation();
  useStore(state => state.render);
  const eraserType = useStore(state => state.eraserType);

  const { ref } = store;
  const { menu, brush, info, work, premium } = ref;
  const { game, user } = info;

  const eraserMenuRef = React.useRef(null);
  const deleteZoneRef = React.useRef(null);
  const [isOverDeleteZone, setIsOverDeleteZone] = useState(false);

  const erasersInGroupsCount = () => {
    const counts = {};
    const category = getEraserCategory();
    const allErasers = {
      basic: allBasicErasers,
      custom: allCustomErasers,
      market: allMarketErasers,
    }[category];

    const groupsKey = `${category}BrushGroups`;
    const groups = user[groupsKey] || {};

    allErasers.forEach(eraser => {
      const settings = customBrushSetsFun(eraser.key);
      if (settings.groupId && groups[settings.groupId]) {
        if (!counts[settings.groupId]) {
          counts[settings.groupId] = 0;
        }
        counts[settings.groupId]++;
      }
    });
    return counts;
  };

  const {
    premiumFeatures = [],
  } = premium;

  const {
    openMenu,
    closeMenu,
    delayPremiumMenu,
    showPopupMessage,
    showTemporaryHint,
  } = menu.methods;

  const {
    saveBrush,
    saveUserSets,
    deleteBrush,
  } = work.methods;

  const {
    customBrushSetsFun,
    changeEraserType,
  } = brush.methods;

  const freeBrushes = ['liner', 'plain', 'pixels'];

  const brushItemAvailabilityStyle = (currentEraserType) => {
    if (eraserType === currentEraserType) return 'active';
    if (premiumFeatures.includes('brushes')) return '';
    if (freeBrushes.includes(currentEraserType)) return '';
    return 'not-available';
  };

  // Разделение ластиков на категории
  const getErasers = () => {
    // ИЗМЕНЕНИЕ: Добавляем 'mafia' в условие
    if (['sprint', 'mafia'].includes(game.mode)) {
      return {
        basicErasers: [
          'plain', 'pixels', 'feather', 'ink', 'shape', 'outlined', 'dashed' 
        ].map(key => ({
          type: 'brush',
          key,
          label: t(`brush.${key}.name`),
          icon: brushIcons[key],
        })),
        customErasers: [],
        marketErasers: [],
        allBasicErasers: [],
        allCustomErasers: [],
        allMarketErasers: [],
      };
    }

    const applySortOrder = (items, orderArray) => {
      if (!orderArray || orderArray.length === 0) return items;
      const itemMap = new Map(items.map(b => [b.key, b]));
      const sorted = orderArray.map(key => itemMap.get(key)).filter(Boolean);
      const unsorted = items.filter(b => !orderArray.includes(b.key));
      return [...sorted, ...unsorted];
    };

    const structureItems = (erasers, groupsObject = {}, eraserOrder) => {
      const groupsArray = Object.entries(groupsObject).map(([id, group]) => ({
        ...group,
        key: id,
        id,
        type: 'group',
      }));

      const erasersInGroups = {};
      const erasersWithoutGroup = [];

      erasers.forEach(eraserItem => {
        const eraserSettings = customBrushSetsFun(eraserItem.key);
        if (eraserSettings.groupId && groupsObject[eraserSettings.groupId]) {
          erasersInGroups[eraserSettings.groupId] ??= []
          erasersInGroups[eraserSettings.groupId].push(eraserItem);
        } else {
          erasersWithoutGroup.push(eraserItem);
        }
      });

      // Создаем единый массив для сортировки групп и ластиков верхнего уровня
      const topLevelItems = [...groupsArray, ...erasersWithoutGroup];
      const sortedTopLevelItems = applySortOrder(topLevelItems, eraserOrder);

      const finalStructure = [];
      sortedTopLevelItems.forEach(item => {
        if (item.type === 'group') {
          finalStructure.push(item);
          if (item.expanded) {
            const sortedErasersInGroup = applySortOrder(erasersInGroups[item.id] || [], eraserOrder);
            finalStructure.push(...sortedErasersInGroup);
          }
        } else {
          finalStructure.push(item);
        }
      });

      return finalStructure;
    };

    const allBasicErasers = [
      'liner', 'plain', 'pixels', 'shape', 'spray', 'bristle', 'wet', 'velvetPastel', 'waterSoft', 'waterSpread', 'flat', 'feather', 'ink',
      'pencil', 'oil', 'rembrandt', 'dashed', 'neon', 'sparkle', 'glyph'
    ].map(key => ({
      type: 'brush',
      key,
      label: t(`brush.${key}.name`),
      icon: brushIcons[key],
    }));
    const basicErasers = structureItems(allBasicErasers, user.basicBrushGroups, user.basicBrushOrder);

    const savedBrushes = user.savedBrushes || [];
    const allCustomErasers = savedBrushes.map(key => {
      const brushSettings = customBrushSetsFun(key);
      const basicBrushLink = brushSettings.clone;
      
      if (!brushIcons[basicBrushLink]) {
        console.log(basicBrushLink);
      }
      
      return {
        type: 'brush',
        key,
        label: brushSettings.label,
        icon: brushIcons[basicBrushLink] || brushIcons.error,
      };
    });
    const customErasers = structureItems(allCustomErasers, user.customBrushGroups, user.customBrushOrder);

    let boughtBrushes = user.boughtBrushes || [];

    const boughtBrushesFix = Object.keys(user.brush || {})
    .filter(brushKey=>!boughtBrushes.includes(brushKey) && user.brush?.[brushKey]?.author);

    boughtBrushes = boughtBrushes.concat(boughtBrushesFix)

    const allMarketErasers = boughtBrushes.map(key => {
      const brushSettings = customBrushSetsFun(key);
      const basicBrushLink = brushSettings.clone;
      return {
        type: 'brush',
        key,
        label: brushSettings.label,
        icon: brushIcons[basicBrushLink] || brushIcons.error,
      };
    });
    const marketErasers = structureItems(allMarketErasers, user.marketBrushGroups, user.marketBrushOrder);

    return {
      basicErasers,
      customErasers,
      marketErasers,
      allBasicErasers,
      allCustomErasers,
      allMarketErasers,
    };
  };

  const { basicErasers, customErasers, marketErasers, allBasicErasers, allCustomErasers, allMarketErasers } = getErasers();
  const currentErasers = 
  user.eraserCat === 'market' ? marketErasers :
  user.eraserCat === 'custom' ? customErasers :
  basicErasers;

  const handleOverlayClick = () => {
    if (brush.lastActionTime < Date.now() - 300) {
      closeMenu();
    }
  };

  const handleCreateGroup = () => {
    showPopupMessage({
      title: t('brush.group.create_title'),
      text: t('brush.group.create_text'),
      input: {
        placeholder: t('brush.group.create_placeholder'),
      },
      buttons: [
        { type: 'cancel', text: t('customize.button_cancel') },
        { id: 'submit', type: 'submit', text: t('customize.button_submit') },
      ],
      callback: (buttonId, inputValue) => {
        if (buttonId === 'submit' && inputValue) {
          window.croco?.haptic?.();
          const category = getEraserCategory();
          const groupsKey = `${category}BrushGroups`;
          const eraserOrderKey = `${category}BrushOrder`;
          const groups = user[groupsKey] || {};
          const currentOrder = user[eraserOrderKey] || [];
          
          const newGroupId = new ObjectID().toHexString();
          const newGroup = {
            title: inputValue,
            expanded: true,
          };

          // Добавляем новую группу в начало порядка
          const newOrder = [newGroupId, ...currentOrder];

          saveUserSets({
            [groupsKey]: { ...groups, [newGroupId]: newGroup },
            [eraserOrderKey]: newOrder
          });
          menu.methods.render();
        }
      }
    });
  };

     const handleToggleGroup = (groupId) => {
     const category = getEraserCategory();
     const groupsKey = `${category}BrushGroups`;
     const groups = { ...(user[groupsKey] || {}) };

    if (groups[groupId]) {
      groups[groupId] = {
        ...groups[groupId],
        expanded: !groups[groupId].expanded,
      };
      saveUserSets({ [groupsKey]: groups });
      menu.methods.render();
    }
  };

  const handleSwitchBrush = (newEraserType) => {
    if (brush.lastActionTime > Date.now() - 300) return;

    if (!freeBrushes.includes(newEraserType) && 
        (!premiumFeatures.includes('brushes') || user.premiumOff)) {
      closeMenu();
      delayPremiumMenu('menu');
      return;
    }

    if (eraserType !== newEraserType) {
      window.croco?.appleHaptic('medium');
      window.croco?.androidHaptic();
    }

    changeEraserType(newEraserType);
    if (newEraserType !== 'spray') {
      useStore.setState({ softness: 0 });
    }
    brush.brushHistory.push(newEraserType);
    closeMenu();
  };

  const handleOpenBrushSettings = (newEraserType, e) => {
    e?.preventDefault();
    e?.stopPropagation();

    brush.lastActionTime = Date.now();
    openMenu('customizeBrush', { brush: newEraserType, layer: true, backMenu: 'eraser' });

    if (newEraserType !== 'plain' && 
        (!premiumFeatures.includes('brushes') || user.premiumOff)) {
      return;
    }

    if (newEraserType !== 'spray') {
      useStore.setState({ softness: 0 });
    }

    if (newEraserType !== 'common') {
      brush.brushHistory.push(newEraserType);
      changeEraserType(newEraserType);
    }
  };

  const getEraserCategory = () => {
    if (user.eraserCat === 'custom') return 'custom';
    if (user.eraserCat === 'market') return 'market';
    return 'basic';
  }

  const handleDeleteItem = (itemId) => {
    const category = getEraserCategory();

    const item = currentErasers.find(i => i.key === itemId);
    if (!item) return;

    const isUserBrush = user.savedBrushes?.includes(item.key);
    const isMarketBrush = user.boughtBrushes?.includes(item.key);

    if (item.type === 'brush' && !isUserBrush && !isMarketBrush) {
      showTemporaryHint(t('brush.group.cannot_delete_basic'));
      return;
    }

    const performDelete = () => {

             if (item.type === 'group') {
         const groupsKey = `${category}BrushGroups`;
         const currentGroups = { ...(user[groupsKey] || {}) };
         delete currentGroups[item.key];
         saveUserSets({ [groupsKey]: currentGroups });

      } else if (item.type === 'brush') {

        user.savedBrushes ??= [];
        user.boughtBrushes ??= [];

        user.savedBrushes = user.savedBrushes.filter(key=>key !== item.key);
        user.boughtBrushes = user.boughtBrushes.filter(key=>key !== item.key);

        info.brush[item.key] = null;
        deleteBrush(item.key);

      }
      menu.methods.render();
      window.croco?.haptic?.();
    };
    
    showPopupMessage({
      title: t('customize.delete_brush_title', { label: item.label || item.title }),
      text: item.type === 'group' ? t('customize.delete_group_text', { label: item.label || item.title }) : t('customize.delete_brush_text', { label: item.label || item.title }),
      buttons: [
        { type: 'cancel', text: t('customize.button_cancel') },
        { id: 'delete', type: 'destructive', text: t('customize.button_delete_confirm') },
      ],
      callback: (buttonId) => {
        if (buttonId === 'delete') {
          performDelete();
        }
      },
    });
  };

  const onSort = (sortedItems) => {
    // Это предотвратит срабатывание клика и закрытие меню
    brush.lastActionTime = Date.now();

    const category = getEraserCategory();
    const eraserOrderKey = `${category}BrushOrder`;

    const finalEraserOrder = sortedItems.map(i => i.key);

    saveUserSets({
      [eraserOrderKey]: finalEraserOrder,
    });

    menu.methods.render();
    menu.methods.appleHaptic?.('light');

  };

  const onMove = (draggedId, targetId) => {
    // Это предотвратит срабатывание клика и закрытие меню
    brush.lastActionTime = Date.now();

    const targetItem = currentErasers.find(i => i.key === targetId);
    const draggedItem = currentErasers.find(i => i.key === draggedId);
   
         // Перемещение ластика в группу
     if (draggedItem?.type === 'brush' && targetItem?.type === 'group') {
      const brushSettings = customBrushSetsFun(draggedId);
      brushSettings.groupId = targetId;
      saveBrush({ [draggedId]: brushSettings });

      setTimeout(() => menu.methods.render(), 50);
      return true; // Сообщаем хуку, что мы обработали событие
    }

         // Перемещение ластика к другому ластику (наследует его groupId)
     if (draggedItem?.type === 'brush' && targetItem?.type === 'brush') {
       const category = getEraserCategory();
       const groupsKey = `${category}BrushGroups`;
       const groups = { ...(user[groupsKey] || {}) };
      
      const targetBrushSettings = customBrushSetsFun(targetId);
      const targetGroupId = targetBrushSettings.groupId || null;
      
      // Если целевой ластик в группе, разворачиваем эту группу
      if (targetGroupId && groups[targetGroupId] && !groups[targetGroupId].expanded) {
        groups[targetGroupId] = { ...groups[targetGroupId], expanded: true };
      }
      
      const draggedBrushSettings = customBrushSetsFun(draggedId);
      draggedBrushSettings.groupId = targetGroupId;
      saveBrush({ [draggedId]: draggedBrushSettings });

      if (targetGroupId) {
        saveUserSets({ [groupsKey]: groups }); 
      }
      setTimeout(() => menu.methods.render(), 50);
      return false; 
    }

    return false; // Мы не обрабатываем это событие, хук должен заняться сортировкой
  };

  const { draggingId, dragOverId, dragDirection, isDraggingActive, handlePointerDownToStartDrag, itemRefs } = useDragAndDropSort({
    items: currentErasers,
    onSort,
    onMove,
    containerRef: eraserMenuRef,
    keyExtractor: (item) => item.key,
    deleteZoneRef,
    onDelete: handleDeleteItem,
    setIsOverDeleteZone,
    needToStopPropagation: true,
  });

  const isEraserInGroup = (oneItem) => {
    if (oneItem.type !== 'brush') return false;
    const brushSettings = customBrushSetsFun(oneItem.key);
    if (!brushSettings.groupId) return false;

         const category = getEraserCategory();
     const groupsKey = `${category}BrushGroups`;
     const groups = user[groupsKey] || {};
     return !!groups[brushSettings.groupId];
  };

  return (
    <>
      <div className="overlay-background" onClick={handleOverlayClick}></div>
      <div className="brush-menu">

        <div className="brush-menu-header">
           <button className="brush-menu-action-btn" onClick={handleCreateGroup}>
             <FaFolderPlus />
           </button>
           <span 
              className="brush-menu-title"
              {...createBetterClick({
                onClick: (e) => handleOpenBrushSettings('common', e),
                onContextMenu: (e) => handleOpenBrushSettings('common', e),
              })}
            >
             {t('brush.eraser_title')}
             <button className="brush-menu-action-btn" onClick={(e) => handleOpenBrushSettings('common', e)}>
             <FaCog style={{ paddingLeft: '10px', }} />
           </button>
           </span>
           <div className="brush-menu-action-btn">
             <div 
                ref={deleteZoneRef} 
                className={`delete-drop-zone ${isOverDeleteZone ? 'active' : ''} ${isDraggingActive ? 'visible' : ''}`}
             >
                <FaTrash/>
             </div>
           </div>
         </div>

        <div className="brush-menu-list add-scroll touch-active" ref={eraserMenuRef}>
          {currentErasers.map((oneItem) => (
             oneItem.type === 'group' ? (
              <div
                key={oneItem.key}
                ref={el => (itemRefs.current[oneItem.key] = el)}
                data-draggable-id={oneItem.key}
                className={`brush-group-row ${isDraggingActive && draggingId === oneItem.key ? 'dragging' : ''} ${dragOverId === oneItem.key && currentErasers.find(i=>i.key === draggingId)?.type === 'brush' ? 'drag-over-group' : ''} ${dragOverId === oneItem.key && !(currentErasers.find(i=>i.key === draggingId)?.type === 'brush') ? `drag-over drag-over-${dragDirection}` : ''} ${oneItem.expanded ? 'expanded' : ''}`}
                onClick={() => handleToggleGroup(oneItem.key)}
              >
                <span className="brush-group-toggle">
                  {oneItem.expanded ? <FaMinus /> : <FaPlus />}
                </span>
                <span className="brush-group-label">
                  {oneItem.title}
                  <span className="brush-group-label-count">
                    ({erasersInGroupsCount()[oneItem.key] || 0})
                  </span>
                </span>
                 <div className="brush-drag-handle" style={{fontSize: '18px'}} onPointerDown={(e) => handlePointerDownToStartDrag(e, oneItem.key)} onClick={(e) => e.stopPropagation()}>
                  ☰
                </div>
              </div>
             ) : (
            <div
              key={oneItem.key}
              ref={el => (itemRefs.current[oneItem.key] = el)}
              data-draggable-id={oneItem.key}
              className={`brush-option-row ${isEraserInGroup(oneItem) ? 'in-group' : ''} ${brushItemAvailabilityStyle(oneItem.key)} ${isDraggingActive && draggingId === oneItem.key ? 'dragging' : ''} ${dragOverId === oneItem.key ? `drag-over drag-over-${dragDirection}` : ''}`}
              {...createBetterClick({
                onClick: () => handleSwitchBrush(oneItem.key),
                onContextMenu: (e) => handleOpenBrushSettings(oneItem.key, e),
              })}
            >
              <label className="brush-label">
                <oneItem.icon className={`brush-icon ${brushItemAvailabilityStyle(oneItem.key)}`} />
                <span> {oneItem.label}</span>
                {eraserType === oneItem.key && (
                  <FaCog
                    className="brush-settings-icon"
                    {...createBetterClick({
                      onClick: (e) => handleOpenBrushSettings(oneItem.key, e),
                      onContextMenu: (e) => handleOpenBrushSettings(oneItem.key, e),
                    })}
                  />
                )}
              </label>
               <div className="brush-drag-handle" onPointerDown={(e) => handlePointerDownToStartDrag(e, oneItem.key)} onClick={(e) => e.stopPropagation()}>
                  ☰
                </div>
            </div>
             )
           ))}
        </div>

        {/* Переключатель категорий */}
        <div className="brush-category-switcher">
          <button
            className={`category-btn ${!['custom', 'market'].includes(user.eraserCat) ? 'active' : ''}`}
            onClick={() => saveUserSets({eraserCat: 'basic'})}
          >
            {t('brush.category.basic')}
          </button>
          <button
            className={`category-btn ${user.eraserCat === 'custom' ? 'active' : ''}`}
            onClick={() => saveUserSets({eraserCat: 'custom'})}
          >
            {t('brush.category.custom')}
          </button>
          { marketErasers.length > 0 && (
            <button
            className={`category-btn ${user.eraserCat === 'market' ? 'active' : ''}`}
            onClick={() => saveUserSets({eraserCat: 'market'})}
          >
            {t('brush.category.market')}
          </button>
          )}
        </div>
      </div>
    </>
  );
}

export default EraserMenu;